import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Tag, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * RelatedSemantic Component - v3.5 Smart Related Articles
 * 
 * Displays articles in two sections:
 * 1. 🔍 Smart Related (semantic embeddings - AI-powered)
 * 2. 📌 More From This Tag (tag-based fallback)
 * 
 * Uses semanticRelated field from embeddings, falls back to tags.
 */
export const RelatedSemantic = ({
    currentArticle,
    allArticles = [],
    maxSemantic = 4,
    maxTagBased = 3
}) => {
    if (!currentArticle || !allArticles.length) return null;

    // 1. Get semantic related articles (from embeddings)
    const semanticRelated = (currentArticle.semanticRelated || [])
        .filter(rel => rel.score > 0.3)
        .slice(0, maxSemantic)
        .map(rel => allArticles.find(a => a.slug === rel.slug))
        .filter(Boolean);

    // 2. Get tag-based related articles (fallback)
    const currentTags = new Set(currentArticle.tags || []);
    const tagRelated = allArticles
        .filter(a =>
            a.id !== currentArticle.id &&
            !semanticRelated.some(s => s.id === a.id) &&
            a.tags?.some(tag => currentTags.has(tag))
        )
        .slice(0, maxTagBased);

    if (semanticRelated.length === 0) return null;

    return (
        <section className="my-12">
            {/* Recommended Reading Section */}
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <h3 className="font-semibold text-lg">Recommended Reading</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {semanticRelated.map((article, index) => (
                        <motion.div
                            key={article.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                        >
                            <Link
                                to={article.canonicalPath || `/blog/${article.slug}`}
                                className="block p-4 rounded-xl bg-gradient-to-br from-primary/5 to-transparent border border-primary/20 hover:border-primary/40 transition-all group"
                            >
                                <h4 className="font-medium group-hover:text-primary transition-colors line-clamp-2 mb-2">
                                    {article.title}
                                </h4>
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <span>{article.readTime}</span>
                                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
            {/* NOTE: Tag-based "More From This Topic" moved to sidebar via RelatedByTags component */}
        </section>
    );
};

export default RelatedSemantic;
